// Income readouts (IMPLEMENTATION §0.2 R16, GDD §6 "Income display"). The economy system closes one bucket
// of `bucketTicks` into the total/auto rings (`ringBuckets` × 1 s) every bucket; `income.idx` is the next
// slot to write, so the newest closed bucket is idx − 1. Manual income is total − auto per bucket (SimState
// §2.2 keeps two rings; a third would only duplicate them). Three kinds of readout:
//   perSec        DISPLAY average over the newest min(span, closed) buckets — fewer while the game is younger
//                 than the span, so the cold open's HUD/ETA are not diluted by empty slots. Used for the total
//                 over the ETA window (60 s: the HUD headline at all times — lead decision L12 — and the goal /
//                 shop / expansion ETA) and manual/s.
//   windowPerSec  FIXED window: the sum over the newest `span` buckets divided by `span` (slots not closed yet
//                 count as $0). Automated income is read this way over the whole ring (120 s) for the u21 /
//                 u30 `autoIncome` trigger ("≥ $1,000/s over the 120 s window"), the u20 goal progress and
//                 `completion` — so one Ammo Box shot in a young game cannot read as $1,000/s (M4a review).
//   hudAutoPerSec YOUNG window (lead decisions L11 / L12, display only): the source of the HUD's secondary
//                 "auto $X/s" line from u18 — the buckets closed since the first automated payout
//                 (`income.autoFrom`), up to the whole ring. The UI smooths it (ui/hudRate.ts); the sim
//                 state never holds a smoothed value.
import { floorDiv } from './core/num.ts';
import type { Int } from './core/types.ts';
import type { SimDefs } from './defs.ts';
import type { SimState } from './state.ts';

export type IncomeRing = 'total' | 'auto' | 'manual';

/** Buckets closed so far, capped at the ring length. */
export function closedBuckets(s: SimState, d: SimDefs): Int {
  const cfg = d.economy.income;
  const n = floorDiv(s.tick, cfg.bucketTicks);
  return n < cfg.ringBuckets ? n : cfg.ringBuckets;
}

/** Sum of the newest `n` closed buckets of a ring (n ≤ ringBuckets). */
function ringSum(s: SimState, d: SimDefs, ring: 'total' | 'auto', n: Int): Int {
  const len = d.economy.income.ringBuckets;
  const xs = ring === 'total' ? s.income.total : s.income.auto;
  let sum = 0;
  for (let k = 1; k <= n; k++) sum += xs[(s.income.idx - k + len) % len] ?? 0;
  return sum;
}

/** Income in $ over the newest min(span, closed) buckets, and the buckets used. */
export function incomeOver(
  s: SimState,
  d: SimDefs,
  ring: IncomeRing,
  span: Int,
): { sum: Int; buckets: Int } {
  const closed = closedBuckets(s, d);
  const n = span < closed ? span : closed;
  const total = ring === 'auto' ? 0 : ringSum(s, d, 'total', n);
  const auto = ring === 'total' ? 0 : ringSum(s, d, 'auto', n);
  const sum = ring === 'total' ? total : ring === 'auto' ? auto : total - auto;
  return { sum, buckets: n };
}

/** Display $/s (floored) over the newest min(span, closed) buckets; 0 before the first bucket closes. */
export function perSec(s: SimState, d: SimDefs, ring: IncomeRing, span: Int): Int {
  const { sum, buckets } = incomeOver(s, d, ring, span);
  if (buckets === 0) return 0;
  return floorDiv(sum * d.tickHz, buckets * d.economy.income.bucketTicks);
}

/** $/s (floored) over a FIXED window of the newest `span` buckets: not-yet-closed slots count as $0. */
export function windowPerSec(s: SimState, d: SimDefs, ring: IncomeRing, span: Int): Int {
  if (span <= 0) return 0;
  const { sum } = incomeOver(s, d, ring, span);
  return floorDiv(sum * d.tickHz, span * d.economy.income.bucketTicks);
}

/**
 * Automated income $/s over the whole ring as a fixed 120 s window (u21 / u30 "automated income ≥ $1,000/s",
 * the u20 goal progress, `completion`).
 */
export function autoPerSec(s: SimState, d: SimDefs): Int {
  return windowPerSec(s, d, 'auto', d.economy.income.ringBuckets);
}

/**
 * Buckets closed since the one that booked the first automated payout (that bucket included), capped at the
 * ring; 0 before the first automated payout or while its bucket is still open. Tick t accumulates into
 * bucket floor((t − 1) / bucketTicks); floor(tick / bucketTicks) buckets have closed.
 */
export function autoYoungBuckets(s: SimState, d: SimDefs): Int {
  const from = s.income.autoFrom;
  if (from < 1) return 0;
  const cfg = d.economy.income;
  const n = floorDiv(s.tick, cfg.bucketTicks) - floorDiv(from - 1, cfg.bucketTicks);
  if (n <= 0) return 0;
  return n < cfg.ringBuckets ? n : cfg.ringBuckets;
}

/**
 * The HUD's secondary auto line source from u18 (young window, L11 / L12): auto income over the buckets closed
 * since the first automated payout, averaged over those buckets (≤ the ring); null while none has closed yet.
 */
export function hudAutoPerSec(s: SimState, d: SimDefs): Int | null {
  const n = autoYoungBuckets(s, d);
  if (n === 0) return null;
  return perSec(s, d, 'auto', n);
}

/** Total income $/s over the ETA window (goal ETA, the HUD headline — always, lead decision L12). */
export function etaPerSec(s: SimState, d: SimDefs): Int {
  return perSec(s, d, 'total', d.economy.income.etaBuckets);
}
